import { useNavigate, useParams } from "@tanstack/react-router";
import { Check, ChevronLeft, Clock } from "lucide-react";

import { useProviderRequest } from "@hooks/queries/use-provider-portal";

import { formatNullableDateToLocal } from "@utils/dateUtils";
import { cn } from "@utils/ui";

const STATUS_PILL: Record<string, { label: string; cls: string }> = {
  DRAFT: { label: "Draft", cls: "bg-[#eef1f6] text-[#5A5A6B]" },
  SUBMITTED: { label: "New", cls: "bg-[#e6f0fb] text-[#0065CB]" },
  IN_PROGRESS: { label: "In progress", cls: "bg-[#fdf0dc] text-[#B26A00]" },
  COMPLETED: { label: "Complete", cls: "bg-[#e7f6ec] text-[#16794F]" },
  FAILED: { label: "Failed", cls: "bg-[#fdecec] text-[#C0392B]" },
};

const REQUEST_TYPE_LABEL: Record<string, string> = {
  records: "Records",
  medical_report: "Medical report",
  work_capacity: "Work capacity",
};

const fmt = (d?: string | null) => formatNullableDateToLocal(d, "MMM d, yyyy", "—");

const Field = ({
  label,
  value,
  full,
}: {
  label: string;
  value?: string | null;
  full?: boolean;
}) => (
  <div className={cn(full && "col-span-2")}>
    <div className="text-[11px] font-bold uppercase tracking-[0.04em] text-[#8A8A98]">
      {label}
    </div>
    <div className="mt-1 text-[13px] text-[#1A1939]">{value || "—"}</div>
  </div>
);

/**
 * Provider portal — request detail (MDRS-157), matching the design layout:
 * a header card (id + status/type + "Raised by" + lifecycle stepper), then a
 * left "What the referrer asked for" card and a right "Records to return"
 * checklist. Only real stored fields are shown — the design's history-period /
 * injury description / TDR questions / attachment bundle aren't persisted yet,
 * so they're omitted; the stepper is derived from the request's real status.
 */
const ProviderRequestDetailPage = () => {
  const navigate = useNavigate();
  const { requestId } = useParams({ strict: false }) as { requestId: string };
  const { data: req, isLoading, isError } = useProviderRequest(requestId);

  const back = () => navigate({ to: "/provider/requests" });

  if (isLoading) {
    return (
      <div className="min-h-full bg-[#F5F7FA] px-[34px] py-[26px] text-sm text-[#5A5A6B]">
        Loading request…
      </div>
    );
  }
  if (isError || !req) {
    return (
      <div className="min-h-full bg-[#F5F7FA] px-[34px] py-[26px]">
        <BackLink onClick={back} />
        <div className="text-sm text-[#C0392B]">
          This request couldn&apos;t be found.
        </div>
      </div>
    );
  }

  const pill = STATUS_PILL[req.status] ?? STATUS_PILL.SUBMITTED;
  const typeLabel = REQUEST_TYPE_LABEL[req.request_type] ?? req.request_type;
  const items = req.items ?? [];
  const receivedCount = req.received_count ?? 0;
  const totalCount = req.total_count ?? 0;

  // Lifecycle stepper derived from the real status + received progress.
  const receiving = receivedCount > 0 || req.status !== "SUBMITTED";
  const complete = req.status === "COMPLETED";
  const steps = [
    { label: "Requested", done: true },
    {
      label: "Retrieving records",
      done: receiving,
      current: !complete && receiving,
    },
    { label: "Records returned", done: complete, current: complete },
  ];

  return (
    <div className="min-h-full bg-[#F5F7FA] px-[34px] py-[26px]">
      <BackLink onClick={back} />

      {/* Header card */}
      <div className="mb-4 overflow-hidden rounded-[14px] border border-[#E2E5EB] bg-white shadow-[0_2px_8px_rgba(26,25,57,0.05)]">
        <div className="flex flex-wrap items-start gap-4 px-6 py-5">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="text-[20px] font-extrabold text-[#1A1939]">
                {req.reference_number ?? "Request"}
              </span>
              <span
                className={cn(
                  "rounded-full px-2.5 py-1 text-[11px] font-bold",
                  pill.cls
                )}
              >
                {pill.label}
              </span>
              <span className="rounded-full bg-[#eef1f6] px-2.5 py-1 text-[11px] font-bold text-[#5A5A6B]">
                {typeLabel}
              </span>
            </div>
            <div className="mt-1.5 text-[13px] text-[#5A5A6B]">
              Claim {req.claim_number ?? "—"} · {req.claimant ?? "—"} · DOB{" "}
              {fmt(req.date_of_birth)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11.5px] text-[#8A8A98]">Raised by</div>
            <div className="mt-0.5 text-[13.5px] font-semibold text-[#1A1939]">
              {req.referrer ?? "—"}
            </div>
            <div className="mt-0.5 text-[11.5px] font-semibold text-[#5A5A6B]">
              Due {fmt(req.due_date)}
            </div>
          </div>
        </div>
        {/* Lifecycle stepper */}
        <div className="flex flex-wrap items-center px-6 pb-[18px] pt-0.5">
          {steps.map((s, i) => (
            <div key={s.label} className="flex items-center">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "flex h-[22px] w-[22px] items-center justify-center rounded-full text-[11px] font-extrabold",
                    s.done
                      ? "bg-[#16794F] text-white"
                      : s.current
                        ? "bg-[#e6f0fb] text-[#0065CB]"
                        : "bg-[#eef1f6] text-[#8A8A98]"
                  )}
                >
                  {s.done ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                <span
                  className={cn(
                    "whitespace-nowrap text-[12px]",
                    s.done || s.current
                      ? "font-semibold text-[#1A1939]"
                      : "text-[#8A8A98]"
                  )}
                >
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <span
                  className={cn(
                    "mx-2.5 h-0.5 w-[34px]",
                    steps[i + 1].done || steps[i + 1].current
                      ? "bg-[#16794F]"
                      : "bg-[#E2E5EB]"
                  )}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Two-column body */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1.15fr_1fr]">
        {/* LEFT — what the referrer asked for */}
        <div className="overflow-hidden rounded-[14px] border border-[#E2E5EB] bg-white shadow-[0_2px_8px_rgba(26,25,57,0.05)]">
          <div className="border-b border-[#eef1f6] px-[22px] py-[15px] text-[14.5px] font-bold text-[#1A1939]">
            What the referrer asked for
          </div>
          <div className="grid grid-cols-2 gap-[15px] px-[22px] py-4">
            <Field label="Claimant" value={req.claimant} />
            <Field label="Date of birth" value={fmt(req.date_of_birth)} />
            <Field label="Employer" value={req.employer_name} />
            <Field label="Date of injury" value={fmt(req.date_of_injury)} />
            <Field label="Request type" value={typeLabel} />
            <Field label="Service type" value={req.service_type} />
            <div className="col-span-2">
              <div className="text-[11px] font-bold uppercase tracking-[0.04em] text-[#8A8A98]">
                Services requested
              </div>
              <div className="mt-[7px] flex flex-wrap gap-[7px]">
                {items.length ? (
                  items.map((it) => (
                    <span
                      key={it.code}
                      className="rounded-full bg-[#eef3fb] px-[11px] py-[5px] text-[12px] font-semibold text-[#33507e]"
                    >
                      {it.label}
                    </span>
                  ))
                ) : (
                  <span className="text-[13px] text-[#8A8A98]">—</span>
                )}
              </div>
            </div>
          </div>
          {req.comment ? (
            <div className="mx-[22px] mb-4 rounded-[11px] border border-[#d8e6fb] bg-[#f5f9ff] px-3.5 py-3 text-[12.5px] leading-[1.55] text-[#33415C]">
              <span className="font-bold text-[#0065CB]">Referrer comments:</span>{" "}
              {req.comment}
            </div>
          ) : null}
        </div>

        {/* RIGHT — records to return */}
        <div className="overflow-hidden rounded-[14px] border border-[#E2E5EB] bg-white shadow-[0_2px_8px_rgba(26,25,57,0.05)]">
          <div className="flex items-center justify-between border-b border-[#eef1f6] px-[22px] py-[15px]">
            <div className="text-[14.5px] font-bold text-[#1A1939]">
              Records to return
            </div>
            <span className="text-[12px] text-[#5A5A6B]">
              {receivedCount} of {totalCount} received
            </span>
          </div>
          <div className="py-2">
            {items.map((item) => (
              <div
                key={item.code}
                className="flex items-center gap-[13px] border-b border-[#f4f6fa] px-[22px] py-3 last:border-0"
              >
                <span
                  className={cn(
                    "flex h-[34px] w-[34px] items-center justify-center rounded-[9px]",
                    item.received
                      ? "bg-[#e7f6ec] text-[#16794F]"
                      : "bg-[#fdf0dc] text-[#B26A00]"
                  )}
                >
                  {item.received ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Clock className="h-4 w-4" />
                  )}
                </span>
                <div className="min-w-0 flex-1 text-[13px] font-semibold text-[#1A1939]">
                  {item.label}
                </div>
                <span
                  className={cn(
                    "text-[11.5px] font-semibold",
                    item.received ? "text-[#16794F]" : "text-[#8A8A98]"
                  )}
                >
                  {item.received ? "Received" : "Pending"}
                </span>
              </div>
            ))}
            {items.length === 0 && (
              <div className="px-[22px] py-3 text-[13px] text-[#8A8A98]">
                No items on this request.
              </div>
            )}
          </div>
          {/* Consent the referrer attached (real field). */}
          {req.consent_filename ? (
            <div className="border-t border-[#f4f6fa] bg-[#fafbfd] px-[22px] py-3.5">
              <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.04em] text-[#8A8A98]">
                Examinee consent
              </div>
              <div className="text-[12.5px] font-semibold text-[#1A1939]">
                {req.consent_filename}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const BackLink = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="mb-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0065CB]"
  >
    <ChevronLeft className="h-[15px] w-[15px]" /> Back to cases
  </button>
);

export default ProviderRequestDetailPage;
